import React, { useEffect, useState } from "react";
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Modal from "@material-ui/core/Modal";
import { connect } from "react-redux";
import { generateForecastedRevenue, updateForecastedRevenue } from "../../../../store/modules/valuation/actions";
import BVAInput from "../../../../components/BVAInput/BVAInput";
import FinancialHandler from "../../../../handlers/FinancialHandler";
import CurrencyFormatter from "currency-formatter";
import YearBar from "../YearBar";
import Subtitle from "../Subtitle";
import MethodSelection from "../MethodSelection";
import Item from "./Item";
import DoubleChart from "../../../../components/BVAChart/DoubleChart";
import MultiChart from "../../../../components/BVAChart/MultiChart";
import "../modal.css";

function ForecastedRevenue({
  forecastedRevenue,
  dispatchGenerateForecastedRevenue,
  dispatchUpdateForecastedRevenue,
}) {

  const [modalVisibility, setModalVisibility] = useState(false);
  const [checked, setChecked] = useState(false);

  // Mounted
  useEffect(() => {
    dispatchGenerateForecastedRevenue();
  }, []);

  const handleChange = (type, value, date = "") => {
    dispatchUpdateForecastedRevenue(type, value, date);
  };

  const handleModalClose = () => setModalVisibility(false);

  const handleModalOpen = () => {
    setModalVisibility(true);
  }

  const handleButtonClick = () => {
    handleModalOpen();
  };

  const handleOnClickGPT = async () => {
    handleChange("Note", "Generating...");
    const note = await FinancialHandler.fetchGPTForecastNoteGeneration(
      "RevenueForecast",
      forecastedRevenue.method,
      forecastedRevenue.yearsFormatted,
      forecastedRevenue.salesRevenue,
      forecastedRevenue.yearsTotal[forecastedRevenue.yearsTotal.length - 1],
      forecastedRevenue.forecastedValue,
      forecastedRevenue.forecastedRevenue
    );
    handleChange("Note", note);
  }

  //consts for charts
  const linearSearies = forecastedRevenue.salesRevenueArray?.concat([forecastedRevenue.linearValue]);
  const weightsSearies = forecastedRevenue.salesRevenueArray?.concat([forecastedRevenue.weightsValue]);
  const manualSearies = forecastedRevenue.salesRevenueArray?.concat([forecastedRevenue.manualValue]);
  const exponentialSearies = forecastedRevenue.salesRevenueArray?.concat([forecastedRevenue.exponentialValue]);

  return (
    <div>
      {forecastedRevenue ?
        (<div>
          <Modal className="modal" onClose={handleModalClose} open={modalVisibility}>
            <div className="modal-main">
              {checked ?
                <MultiChart
                  label={forecastedRevenue.yearsTotal}
                  title="Forecasted Revenue"
                  yTitle="Revenue"
                  series={[{
                    name: 'Linear Trend',
                    type: 'area',
                    data: linearSearies
                  }, {
                    name: 'Weights',
                    type: 'area',
                    data: weightsSearies
                  }, {
                    name: 'Manual',
                    type: 'area',
                    data: manualSearies
                  }, {
                    name: 'Exponential',
                    type: 'area',
                    data: exponentialSearies
                  }]}
                />
                :
                <DoubleChart
                  label={forecastedRevenue.yearsTotal}
                  title="Forecasted Revenue"
                  yTitle="Revenue"
                  name1="Sales Revenue"
                  name2="YoY Change"
                  series1={forecastedRevenue.salesRevenueArray?.concat([forecastedRevenue.forecastedValue])}
                  series2={forecastedRevenue.yoyArray}
                  minYaxis={forecastedRevenue.minYaxis}
                  forecastedValue={forecastedRevenue.forecastedValue}
                />
              }
              <div className="flex justify-between mx-20 pl-10">
                <FormControlLabel control={<Checkbox checked={checked} onChange={(e) => setChecked(!checked)} />} label="Show all" />
                <MethodSelection value={forecastedRevenue.method} handleChange={(e) => handleChange("Method", e.target.value)} />
              </div>
            </div>
          </Modal>

          <div className="flex items-center justify-between pr-10">
            <h2 className="sectionHead">Forecasted Revenue</h2>
            <MethodSelection value={forecastedRevenue.method} handleChange={(e) => handleChange("Method", e.target.value)} />
          </div>
          
          <div className="pb-10">
            <Subtitle title="Forecasted Revenue" />
            <YearBar data={forecastedRevenue.yearsTotal} />

            {/* Sales Revenue Estimate */}
            <div className="flex items-center font-semibold mt-5">
              <div className="whitespace-nowrap flex items-center" style={{ minWidth: "18em" }}>
                Sales Revenue Estimate
              </div>
              <div className="flex">
                {forecastedRevenue.salesRevenue?.map((obj) => (
                  <div className="flex justify-center" style={{ minWidth: "10em" }}>
                    {CurrencyFormatter.format(obj?.value, { code: "USD" })}
                  </div>
                ))}
              </div>
              <div className="flex justify-center" style={{ minWidth: "10em" }}>
                {forecastedRevenue.method === "Manual" ?
                  <BVAInput
                    startAdornment
                    startAdornmentLabel="$ "
                    width="95%"
                    size="small"
                    value={forecastedRevenue.forecastedValue}
                    onChange={(value) => handleChange("ManualValue", value)}
                  />
                  :
                  CurrencyFormatter.format(forecastedRevenue.forecastedValue, { code: "USD" })
                }
              </div>
              <button
                className="chartBtn text-xl"
                style={{ minWidth: "3em" }}
                onClick={() => handleButtonClick()}
              >
                &#128202;
              </button>
            </div>

            {/* YoY Change */}
            <div className="flex mt-5">
              <div className="whitespace-nowrap flex" style={{ minWidth: "18em" }}>
                YoY Change
              </div>
              <div className="flex">
                {forecastedRevenue.yoyArray?.map((val) => (
                  <div className="flex justify-center" style={{ minWidth: "10em" }}>
                    {val === null ? "" : val + "%"}
                  </div>
                ))}
              </div>
            </div>

            {/* Weights input */}
            {forecastedRevenue.method === "Weights" ?
              (<div className="flex items-center mt-5">
                <div className="whitespace-nowrap flex items-center" style={{ minWidth: "18em" }}>
                  Weights
                </div>
                <div className="flex">
                  {forecastedRevenue?.weights?.map((item) => (
                    <div className="flex items-center" style={{ width: "10em" }}>
                      <BVAInput
                        startAdornment
                        width="95%"
                        size="small"
                        key={item.date}
                        value={item.value}
                        onChange={(value) => handleChange("Weights", value, item.date)}
                      />
                    </div>
                  ))}
                </div>
              </div>)
              :
              (<div />)
            }

            <Subtitle title="Forecast of Long-term Revenue" />
            <Item name="Gross Revenue Estimate" data={forecastedRevenue.forecastedValue} />
            <Item name="Growth Rate" data={forecastedRevenue.growthRate} margin={true} />
            <Item name="Forecasted Revenue" data={forecastedRevenue.forecastedRevenue} bold={true} />

            {/* Analyst note */}
            <div className="data-bottom pt-5">
              <h5 className="noteHead">Analyst Note&nbsp;&nbsp;&nbsp;<button onClick={() => handleOnClickGPT()}>🤖</button></h5>
              <textarea
                className="analystNote px-3 py-1"
                style={{ width: forecastedRevenue.yearsFormatted?.length * 10 + 27 + "em" }}
                rows={3}
                value={forecastedRevenue.analystNote}
                onChange={(e) => handleChange("Note", e.target.value)}
              />
            </div>
          </div>
        </div>)
        :
        (<div />)
      }
    </div>
  );
}

const mapStateToProps = (state) => ({
  forecastedRevenue: state.valuation.forecastedRevenue,
});

const mapDispatchToProps = (dispatch) => ({
  async dispatchGenerateForecastedRevenue() {
    await dispatch(generateForecastedRevenue());
  },
  async dispatchUpdateForecastedRevenue(type, value, date) {
    await dispatch(updateForecastedRevenue(type, value, date));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(ForecastedRevenue);